import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabaseClient';

// Anchored popover for @-mentions in the brief comments composer. The parent
// (BriefCommentsPanel) opens it when the textarea's caret sits inside an
// open @… fragment, passes the post-@ query as a prop, and receives
// onSelect(user) with the chosen internal_user so it can splice the
// sentinel into the textarea.
//
// Modelled on TagPicker: outside-click / Esc to close, ↑/↓/Enter to
// navigate, no per-keystroke fetch (the internal_users list is loaded once
// and cached on the panel).
//
// Avatar palette is hash-stable per user id so the same person looks the
// same everywhere.
const PALETTE = [
  { bg: '#92D400', fg: '#131313' },  // fusion green
  { bg: '#2E8FA1', fg: '#F9FCF1' },  // fusion teal
  { bg: '#E89A2E', fg: '#131313' },  // fusion amber
  { bg: '#51534A', fg: '#F9FCF1' },  // fusion mid-grey
];

export function avatarPalette(userId) {
  if (!userId) return PALETTE[3];
  let hash = 0;
  for (let i = 0; i < userId.length; i++) hash = (hash * 31 + userId.charCodeAt(i)) | 0;
  return PALETTE[Math.abs(hash) % PALETTE.length];
}

export function initialOf(name) {
  const trimmed = String(name || '').trim();
  if (!trimmed) return '?';
  return trimmed[0].toUpperCase();
}

// Static cache shared across mounts so the picker is instant after the
// first open. Re-fetched on the next mount after a hard refresh.
let CACHE = null;
let CACHE_AT = 0;
const CACHE_TTL_MS = 5 * 60 * 1000;

async function loadActiveInternalUsers() {
  if (CACHE && Date.now() - CACHE_AT < CACHE_TTL_MS) return CACHE;
  const { data } = await supabase
    .from('internal_users')
    .select('id, full_name, job_title, is_active')
    .eq('is_active', true)
    .order('full_name', { ascending: true });
  CACHE = (data || []).filter(u => u.full_name);
  CACHE_AT = Date.now();
  return CACHE;
}

// Bust the cache when membership actually changes (a new user added, an
// existing user disabled). Wire into the admin user-mgmt flow if needed;
// otherwise the 5-min TTL is fine.
export function invalidateMentionUserCache() {
  CACHE = null;
  CACHE_AT = 0;
}

export default function MentionPicker({ query, anchorRect, excludeUserId, onSelect, onClose }) {
  const [users, setUsers] = useState([]);
  const [highlight, setHighlight] = useState(0);
  const containerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    loadActiveInternalUsers().then(list => {
      if (!cancelled) setUsers(list);
    });
    return () => { cancelled = true; };
  }, []);

  // Filter on full_name prefix-style (case-insensitive). Empty query shows
  // the full list — typing @ alone opens the picker pre-loaded.
  const q = String(query || '').trim().toLowerCase();
  const filtered = users
    .filter(u => u.id !== excludeUserId)
    .filter(u => !q || u.full_name.toLowerCase().includes(q))
    .slice(0, 8);

  // Reset the highlight whenever the filter changes shape, so the first
  // visible row is always the default Enter target.
  useEffect(() => { setHighlight(0); }, [q, filtered.length]);

  // Outside-click + Esc close. Arrow + Enter navigation. Listens at
  // document level so the textarea (which keeps focus) still drives the
  // picker via key events bubbled from the parent.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose?.(); return; }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlight(h => Math.min(filtered.length - 1, h + 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlight(h => Math.max(0, h - 1));
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        if (filtered[highlight]) {
          e.preventDefault();
          onSelect?.(filtered[highlight]);
        }
      }
    };
    const onDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) onClose?.();
    };
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('mousedown', onDown);
    };
  }, [filtered, highlight, onSelect, onClose]);

  // Anchor popover above the textarea's bounding rect. The parent passes
  // the rect (textarea.getBoundingClientRect()) so the picker can position
  // without measuring its own anchor.
  const style = anchorRect ? {
    position: 'fixed',
    left: `${anchorRect.left}px`,
    top: `${anchorRect.top - 8}px`,
    transform: 'translateY(-100%)',
    width: '260px',
    zIndex: 60,
  } : { position: 'fixed', left: 16, top: 16, zIndex: 60, width: '260px' };

  return (
    <div
      ref={containerRef}
      style={style}
      className="bg-white border border-[#E6EADB] rounded-xl p-2 shadow-lift"
    >
      <p className="font-['Metropolis'] text-[9px] font-bold tracking-[0.18em] uppercase text-[#51534A] px-2 py-1">Mention team member</p>
      {filtered.length === 0 ? (
        <p className="font-['Metropolis'] text-xs text-[#51534A] px-2 py-2">No matches.</p>
      ) : filtered.map((u, i) => {
        const pal = avatarPalette(u.id);
        return (
          <button
            type="button"
            key={u.id}
            onMouseEnter={() => setHighlight(i)}
            onMouseDown={(e) => { e.preventDefault(); onSelect?.(u); }}
            className={`w-full text-left flex items-center gap-2.5 px-2 py-1.5 rounded-md transition-colors ${i === highlight ? 'bg-[#2E8FA1]/15' : 'hover:bg-[#2E8FA1]/10'}`}
          >
            <span
              className="w-6 h-6 rounded-md flex items-center justify-center font-['Metropolis'] text-[11px] font-bold flex-shrink-0"
              style={{ backgroundColor: pal.bg, color: pal.fg }}
            >{initialOf(u.full_name)}</span>
            <span className="min-w-0">
              <span className="block font-['Metropolis'] text-xs text-ink font-bold truncate">{u.full_name}</span>
              {u.job_title && <span className="block font-['Metropolis'] text-[10px] text-[#51534A] truncate">{u.job_title}</span>}
            </span>
          </button>
        );
      })}
    </div>
  );
}
